import { useState } from "react";
import { motion } from "framer-motion";
import CpiRadar from "../CpiRadar";
import HudCorners from "./HudCorners";
import { CPI_PROFILES } from "@/data/cpi";

export const HeroCpiVisual = () => {
  const [activeId, setActiveId] = useState(CPI_PROFILES[0].id);
  const profile = CPI_PROFILES.find((item) => item.id === activeId) || CPI_PROFILES[0];

  return (
    <motion.div
      initial={{ opacity: 0, x: 24 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.85, ease: [0.22, 1, 0.36, 1], delay: 0.22 }}
      className="lg:col-span-5 relative"
      data-testid="hero-cpi-dashboard"
    >
      <div className="relative overflow-hidden border-2 border-primary bg-foreground text-background shadow-[8px_8px_0_0_rgba(17,41,107,0.14)]">
        <div className="absolute inset-0 iw-grid-bg opacity-[0.08]" aria-hidden />
        <HudCorners offset={10} size={11} />

        <div className="relative flex items-center justify-between border-b border-background/15 px-5 py-3.5">
          <span className="font-mono text-[0.58rem] uppercase tracking-[0.2em] text-background/60">
            CPI.ANALYSIS · SAMPLE
          </span>
          <span className="inline-flex items-center gap-2 font-mono text-[0.56rem] uppercase tracking-[0.18em] text-background/75">
            <span className="h-1.5 w-1.5 bg-[#00FF66] animate-pulse" aria-hidden />
            Profile active
          </span>
        </div>

        <div className="relative grid grid-cols-1 sm:grid-cols-12 gap-2 p-5 md:p-6">
          <div className="sm:col-span-4 flex flex-col justify-between border border-background/15 bg-background/[0.04] p-4">
            <div>
              <div className="font-mono text-[0.58rem] uppercase tracking-[0.18em] text-background/50">Composite CPI</div>
              <div data-testid="hero-cpi-score" className="mt-2 font-display text-5xl font-bold text-background leading-none">
                {profile.cpi}
              </div>
              <div className="mt-1 font-mono text-[0.58rem] uppercase tracking-[0.16em] text-accent">of 100</div>
            </div>
            <div className="mt-6">
              <div className="font-mono text-[0.55rem] uppercase tracking-[0.15em] text-background/45">Pattern</div>
              <div data-testid="hero-cpi-pattern" className="mt-2 font-display text-sm font-bold leading-snug text-background">
                {profile.name}
              </div>
            </div>
          </div>

          <div className="sm:col-span-8 min-w-0">
            <CpiRadar
              domains={profile.domains}
              dark
              testId="hero-cpi-radar"
              ariaLabel={`${profile.name} fictional Clinical Performance Index profile`}
              className="w-full max-w-[300px] mx-auto"
            />
          </div>
        </div>

        <div className="relative border-t border-background/15 px-5 py-4">
          <div className="grid grid-cols-3 gap-1" role="group" aria-label="Explore fictional CPI profiles">
            {CPI_PROFILES.map((item) => {
              const active = item.id === profile.id;
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => setActiveId(item.id)}
                  data-testid={`hero-cpi-profile-${item.id}`}
                  aria-pressed={active}
                  className={`min-h-10 border px-2 py-2 font-mono text-[0.54rem] uppercase tracking-[0.12em] transition-colors ${
                    active
                      ? "border-accent bg-accent text-white"
                      : "border-background/20 text-background/65 hover:border-background/55 hover:text-background"
                  }`}
                >
                  {item.label}
                </button>
              );
            })}
          </div>
          <p data-testid="hero-cpi-summary" className="mt-4 text-sm leading-relaxed text-background/68">
            {profile.summary}
          </p>
          <div data-testid="hero-cpi-priorities" className="mt-3 flex flex-wrap gap-2">
            {profile.priorities.map((priority) => (
              <span key={priority} className="border border-background/15 px-2 py-1 font-mono text-[0.52rem] uppercase tracking-[0.1em] text-background/65">
                {priority}
              </span>
            ))}
          </div>
        </div>
      </div>
      <p data-testid="hero-cpi-disclaimer" className="mt-3 font-mono text-[0.55rem] uppercase tracking-[0.13em] text-foreground/45">
        Fictional profiles for demonstration only · Individual results vary
      </p>
    </motion.div>
  );
};